import { groupBy } from '~/services/PaymentHelper';

import type { CampaignScheduleImportCandidate, CampaignScheduleParentType } from './types';

export type CampaignScheduleUpdateCandidate = CampaignScheduleImportCandidate & {
  conflictingSchedule: NonNullable<CampaignScheduleImportCandidate['conflictingSchedule']>;
};

export interface CampaignScheduleImportGroup {
  parentRowId: string;
  type: CampaignScheduleParentType;
  start: string;
  end: string;
  toCreate: Array<CampaignScheduleImportCandidate>;
  toUpdate: Array<CampaignScheduleUpdateCandidate>;
  updateBatches: Array<Array<CampaignScheduleUpdateCandidate>>;
  canBulkCreate: boolean;
}

function isUpdateCandidate(candidate: CampaignScheduleImportCandidate): candidate is CampaignScheduleUpdateCandidate {
  return !!candidate.conflictingSchedule;
}

// The bulk create endpoint cross-products brands[]/regions[] server-side, so it's only safe when every
// brand x region combination is present — any gap means it would recreate the missing one too.
function coversEveryBrandRegionCombination(candidates: Array<CampaignScheduleImportCandidate>): boolean {
  if (candidates.length === 0) {
    return false;
  }

  const brands = new Set(candidates.map((candidate) => candidate.brand));
  const regions = new Set(candidates.map((candidate) => candidate.region));

  return candidates.length === brands.size * regions.size;
}

// The bulk update endpoint applies one marketing_channels value to every id in the list, so candidates whose
// existing schedules have different values can't share a request
function groupUpdatesByMarketingChannels(
  candidates: Array<CampaignScheduleUpdateCandidate>,
): Array<Array<CampaignScheduleUpdateCandidate>> {
  const batches = groupBy(candidates, (candidate) =>
    JSON.stringify(candidate.conflictingSchedule.marketingChannels ?? []),
  );

  return Array.from(batches.values());
}

export function buildImportGroups(
  selectedCandidates: Array<CampaignScheduleImportCandidate>,
): Array<CampaignScheduleImportGroup> {
  const selectedByParentRowId = groupBy(selectedCandidates, (candidate) => candidate.parentRowId);

  return Array.from(selectedByParentRowId.entries()).map(([parentRowId, candidates]) => {
    const toUpdate = candidates.filter(isUpdateCandidate);
    const toCreate = candidates.filter((candidate) => !isUpdateCandidate(candidate));

    return {
      parentRowId,
      type: candidates[0].type,
      start: candidates[0].start,
      end: candidates[0].end,
      toCreate,
      toUpdate,
      updateBatches: groupUpdatesByMarketingChannels(toUpdate),
      canBulkCreate: coversEveryBrandRegionCombination(toCreate),
    };
  });
}
